// src/pages/departments/ElectricalEngineering.js
//
// Electrical Engineering departmental page using the FPB departmental UI standard.
// Departmental narrative, HOD identity, admission requirements, course structure,
// contact details and brochure copy are based on the supplied "Elect Eng. website.docx".
// Staff profiles are extracted from "Copy of Staff Academic Particulars_(1).csv".
//
// Editorial note:
// The supplied admission paragraph says "Chemical Engineering Technology" inside the
// Electrical Engineering document. The programme name is normalised here to
// "Electrical Engineering Technology" while the substantive requirements are preserved.
// The supplied ND course table repeats six courses at the end; the page preserves the
// departmental list exactly as supplied.

import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Award,
  BadgeCheck,
  BatteryCharging,
  BookOpen,
  BookOpenCheck,
  BriefcaseBusiness,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircuitBoard,
  Cpu,
  Download,
  DraftingCompass,
  Factory,
  FileText,
  Gauge,
  GraduationCap,
  Lightbulb,
  Mail,
  MapPin,
  Maximize2,
  Phone,
  Radio,
  Search,
  Settings,
  ShieldCheck,
  Sun,
  Users,
  Users2,
  Wifi,
  Wrench,
  X,
  Zap,
} from "lucide-react";

import {
  ELECTRICAL_STAFF_IMAGE_BASE,
  electricalEngineeringStaff,
} from "./electricalEngineeringStaffData";

const ELECTRICAL_IMAGE_BASE = "/images/departments/electrical-engineering";

const Divider = ({ delay = 0 }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    transition={{ duration: 0.75, delay }}
    viewport={{ once: true, amount: 0.1 }}
    className="origin-left h-1 bg-gradient-to-r from-blue-700 to-cyan-400 rounded-full my-8"
  />
);

const EMPTY_VALUES = new Set([
  "",
  "nil",
  "nill",
  "none",
  "n/a",
  "na",
  "not applicable",
  "not available",
  "-",
]);

const cleanText = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  return EMPTY_VALUES.has(text.toLowerCase()) ? "" : text;
};

const cleanArray = (items = []) =>
  [...new Set(items.map(cleanText).filter(Boolean))];

const fullStaffName = (staff) =>
  `${cleanText(staff?.title)} ${cleanText(staff?.name)}`.trim();

const isUsableEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanText(value));

const isUsablePhone = (value) =>
  cleanText(value).replace(/\D/g, "").length >= 10;

const hodProfile = {
  name: "Engr. Alimi Teslim Adekunle",
  credentials: "MNSE, COREN",
  qualification: "M.Eng Electrical Engineering (FUT Minna)",
  role: "Head of Department",
  image: `${ELECTRICAL_STAFF_IMAGE_BASE}/alimi-teslim-adekunle.jpg`,
  email: "electrical.engineering@fedpolybida.edu.ng",
  phone: "+234 803 637 4037",
};

const departmentHistory = [
  "The Electrical Engineering Department was one of the pioneer departments established in the institution, with a strong academic and professional foundation dating back to 1977.",
  "Following the relocation of the institution from Kano to Bida, Mrs. Kristopher, a Bulgarian national, became the first Head of the Department. She was subsequently succeeded by the department’s first indigenous Head, Engr. I. B. Oladimeji, who later ascended the traditional stool as an Oba. Upon his departure from the department, the leadership was handed over to Engr. Adebanjo.",
  "Over the years, the department has continued to grow significantly in terms of human resources, student enrolment, academic programmes, and infrastructural facilities. The department is currently headed by Engr. Alimi Teslim Adekunle and is supported by a dedicated team comprising thirty-five (35) teaching staff, sixteen (16) technical staff, seventeen (17) laboratory attendants, one (1) secretary, and three (3) clerical officers.",
  "From its inception, the department has maintained a tradition of academic excellence, professional competence, and quality technical education. This tradition has been sustained through the continuous recruitment, training, retraining, and professional development of qualified and dedicated personnel who remain committed to upholding the high standards established by the pioneer members of staff.",
  "As a result of the department’s commitment to academic and professional excellence, the National Diploma (ND) and Higher National Diploma (HND) programmes in Electrical/Electronics Engineering were accredited by the National Board for Technical Education (NBTE) in 1985, less than a decade after the establishment of the Polytechnic.",
  "The department commenced with a pioneer student enrolment of fewer than thirty (30) students. Over the years, it has experienced remarkable growth and currently has a student population of over 500 students per academic session, with an average of approximately 300 graduates produced annually. This significant growth is largely attributable to the outstanding performance and contributions of the department’s graduates in various sectors of the economy.",
  "In addition to the National Diploma and Higher National Diploma programmes, the department also commenced the Bachelor of Engineering (B.Eng.) in Electrical/Electronics Engineering programme, affiliated with the Federal University of Technology, Minna (FUTMinna) Niger State. The programme received accreditation in 2018, thereby expanding the department’s academic offerings and providing opportunities for students to pursue degree-level education within the institution. The programme currently has a student population of approximately 137 students.",
  "Despite the geographical distance of the institution from the major industrial centres of the country, the department has continued to attract students because of its reputation for quality education, practical training, and professional development. Parents and industrial stakeholders have consistently demonstrated confidence in the department by encouraging their wards and staff members, respectively, to pursue studies in Electrical Engineering.",
  "A significant milestone in the development of the department was recorded in 1989, when the Federal Polytechnic, Bida was selected as a Centre of Excellence in Electrical/Electronic Engineering under an educational equipment support programme funded through a World Bank Loan. The equipment, which arrived in 1990, provided a major boost to the department’s teaching, practical training, research, and laboratory facilities.",
  "The educational equipment covered virtually all major areas of Electrical Engineering and facilitated the establishment and strengthening of standard laboratories, including Electrical Power, Electrical Machines, Telecommunications, Electronics, Instrumentation and Control, Computer, and Basic Electricity Laboratories.",
  "The provision of these facilities has significantly enhanced the quality of teaching, practical training, research, and student project development within the department. It has also contributed substantially to the department’s ability to achieve the goals, objectives, and standards prescribed by the National Board for Technical Education (NBTE) for Electrical Engineering programmes.",
  "The department has also benefited from equipment procurement initiatives of the Education Trust Fund (ETF) and Tetfund, which supported the provision of educational facilities and equipment to tertiary institutions across the country. These interventions have further strengthened the department’s capacity to provide effective practical and technology-oriented education.",
  "In addition to infrastructural and laboratory development, the department has consistently encouraged staff development, academic advancement, and professional growth. Several members of staff have benefited from scholarship awards and sponsorship opportunities to pursue higher degrees and undertake further professional development. Similarly, students of the department have benefited from various merit-based awards and scholarship schemes, including MTN scholarships and the Rector’s Honour List, among others.",
  "Over the years, the Electrical Engineering Department has remained committed to its core mandate of producing competent, innovative, and professionally oriented Electrical Engineering graduates capable of contributing meaningfully to national development. Through sustained investment in human capacity development, modern laboratory facilities, quality teaching, research, practical training, and industry engagement, the department continues to build upon the legacy established by its pioneer staff and maintain its position as an important centre for Electrical Engineering education and technological development.",
  "The department looks forward to further expanding its academic programmes, strengthening industry collaboration, improving its laboratory and research facilities, and developing highly skilled graduates who can respond effectively to emerging technological challenges and the evolving needs of the national and global economy."
];

const hodSpeechParagraphs = [
  "Prospective students are welcome to the Department of Electrical Engineering Technology, The Federal Polytechnic, Bida. Niger State, Nigeria.",
  "Choosing a course of study is an important decision that can shape your future, and Electrical Engineering Technology offers an exciting opportunity to build a career in one of the most dynamic and technology-driven professions in the world.",
  "Electrical Engineering is at the centre of modern development. From power generation and distribution to telecommunications, electronics, automation, renewable energy, industrial systems, computer technology, and smart devices, electrical engineering professionals contribute significantly to almost every sector of society.",
  "As a Polytechnic, we place great emphasis on hands-on learning, technical competence, innovation, creativity, and the ability to apply engineering knowledge to real-life challenges.",
  "Students in the Department are exposed to important areas such as electrical machines, power systems, electronics, telecommunications, instrumentation, control systems, digital technology, renewable energy, electrical installation, circuit design, and computer applications.",
  "Through classroom teaching, laboratory practicals, workshops, technical projects, industrial training, and field experience, our students develop the skills and confidence required to become competent professionals in the electrical and electronics industry.",
  "The world of engineering is changing rapidly, and new technologies continue to reshape the way we live and work. Today, electrical engineering professionals are involved in solar and renewable energy systems, automation, robotics, smart grids, embedded systems, Internet of Things applications, artificial intelligence-supported technologies, and modern communication systems. Our goal is to prepare students who can adapt to these changes and contribute meaningfully to technological advancement.",
  "Electrical Engineering Technology is rewarding, but it requires discipline, curiosity, commitment, creativity, and a willingness to learn. Prospective students should develop a strong interest in Mathematics, Physics, technology, problem-solving, and practical work.",
  "We also believe that education goes beyond obtaining a certificate. Technical knowledge is important, but integrity, professionalism, teamwork, responsibility, safety consciousness, communication skills, and good character are equally essential for success.",
  "Graduates of Electrical Engineering Technology can find opportunities in power generation and distribution companies, telecommunications firms, manufacturing industries, oil and gas companies, renewable energy organisations, construction firms, ICT companies, government agencies, educational institutions, and many other public and private organisations.",
  "Graduates may also become entrepreneurs by establishing businesses in electrical installation, solar energy systems, electronics maintenance, automation, equipment servicing, telecommunications, and related technology services.",
  "To every prospective student, I encourage you to see Electrical Engineering Technology as more than a course of study. It is an opportunity to acquire practical skills, develop innovative ideas, solve problems, and contribute to the technological growth of society.",
  "If you are passionate about electricity, electronics, technology, innovation, and creating solutions that improve lives, then Electrical Engineering Technology may be the right choice for you.",
  "We look forward to welcoming hardworking, creative, disciplined, and ambitious students into our academic community.",
  "Welcome to the Department of Electrical Engineering Technology — where knowledge meets innovation, practical skills create opportunities, and today’s students are prepared to power the future."
];

const whyStudyIntro = "Studying Electrical Engineering Technology in The Federal Polytechnic, Bida provides students with a strong combination of theoretical knowledge, practical skills, technological exposure, and industry-relevant training.";

const studyReasons = [
  {
    icon: Wrench,
    title: "Hands-on Practical Training",
    description: "Students gain experience through laboratories, workshops, technical projects, electrical installations, and industrial training.",
  },
  {
    icon: CircuitBoard,
    title: "Industry-Relevant Curriculum",
    description: "Our programme covers power systems, electronics, telecommunications, electrical machines, control systems, instrumentation, and renewable energy technologies.",
  },
  {
    icon: Cpu,
    title: "Modern Technology Exposure",
    description: "Students are introduced to automation, solar technology, computer-aided design, programmable controllers, embedded systems, and other emerging technologies.",
  },
  {
    icon: Users2,
    title: "Experienced Academic Staff",
    description: "Students receive professional guidance, practical instruction, and mentorship from qualified lecturers and technologists.",
  },
  {
    icon: Lightbulb,
    title: "Entrepreneurship Development",
    description: "The programme encourages students to develop skills for establishing electrical, electronics, renewable-energy, and technology-related businesses.",
  },
  {
    icon: BriefcaseBusiness,
    title: "Career Preparation",
    description: "Graduates are equipped with practical and professional skills required to compete effectively in today's technology-driven workplace.",
  },
  {
    icon: GraduationCap,
    title: "Further Education",
    description: "The programme provides a strong foundation for advanced studies and professional development in specialised engineering fields.",
  },
];

const whyStudyClosing = "Study Electrical Engineering Technology with us — develop practical skills, create innovative solutions, and prepare to power the future.";

const jobProspectsIntro = "Electrical Engineering Technology graduates can pursue careers across power, telecommunications, manufacturing, construction, renewable energy, ICT, oil and gas, transportation, and other technology-driven industries.";

const careerOpportunities = [
  "Electrical Engineering Technologist",
  "Electrical Installation and Maintenance Technologist",
  "Power Systems Technologist",
  "Electronics Technologist",
  "Telecommunications Technologist",
  "Control and Instrumentation Technologist",
  "Automation/PLC Technician or Technologist",
  "Solar and Renewable Energy Technologist",
  "Electrical Maintenance Officer",
  "Electrical CAD Technician",
  "Industrial Maintenance Technologist",
  "Technical Support Officer",
  "Quality Control and Testing Officer",
  "Electrical Project Supervisor",
  "Technical Officer in Government Ministries and Agencies",
  "Electrical Laboratory Technologist",
  "Entrepreneur/Electrical Contractor"
];

const jobProspectsClosing = [
  "Graduates may work with electricity generation and distribution companies, telecommunications organizations, manufacturing industries, construction companies, renewable energy firms, oil and gas companies, ICT organizations, government agencies, educational institutions, and other public and private establishments.",
  "With further education, professional certification, and experience, graduates can progress into specialized, supervisory, consultancy, managerial, and entrepreneurial roles."
];

const professionalDevelopmentIntro = "Students and graduates are encouraged to complement their Polytechnic qualifications with relevant professional registration, certifications, and specialized technical training.";

const professionalCourses = [
  {
    icon: ShieldCheck,
    title: "COREN Registration – Engineering Technologist",
    description: "Appropriate graduates can pursue registration with the Council for the Regulation of Engineering in Nigeria under its Engineering Technologist category, subject to its requirements.",
  },
  {
    icon: CircuitBoard,
    title: "AutoCAD Electrical",
    description: "",
  },
  {
    icon: Cpu,
    title: "Autodesk Revit for Electrical Design",
    description: "Autodesk provides professional certification for advanced Revit electrical-design skills.",
  },
  {
    icon: Settings,
    title: "Programmable Logic Controllers (PLC) and SCADA",
    description: "",
  },
  {
    icon: Sun,
    title: "Industrial Automation and Control Systems",
    description: "",
  },
  {
    icon: Wrench,
    title: "Solar PV and Renewable Energy Systems",
    description: "",
  },
  {
    icon: Gauge,
    title: "Electrical Installation and Maintenance",
    description: "",
  },
  {
    icon: Zap,
    title: "Instrumentation and Process Control",
    description: "",
  },
  {
    icon: ShieldCheck,
    title: "Power Systems and Protection",
    description: "",
  },
  {
    icon: Cpu,
    title: "High Voltage and Electrical Safety",
    description: "",
  },
  {
    icon: CircuitBoard,
    title: "Health, Safety and Environment (HSE)",
    description: "",
  },
  {
    icon: Radio,
    title: "Internet of Things (IoT) and Embedded Systems",
    description: "",
  },
  {
    icon: BriefcaseBusiness,
    title: "Electrical CAD Design and Drafting",
    description: "",
  },
  {
    icon: BadgeCheck,
    title: "Networking and Telecommunications",
    description: "",
  },
  {
    icon: BatteryCharging,
    title: "Project Management",
    description: "Early-career graduates may consider CAPM, while experienced professionals may progress toward PMP after meeting PMI's experience and training requirements.",
  },
  {
    icon: Lightbulb,
    title: "Quality Assurance and Quality Control (QA/QC)",
    description: "",
  },
  {
    icon: BookOpen,
    title: "Energy Management and Energy Auditing",
    description: "",
  },
];

const admissionIntro = "Candidates applying for admission to study Electrical Engineering Technology, either at National Diploma – ND or Higher National Diploma – HND programme are required to have a minimum of five credits in O’Level at not more than two sittings with Mathematics, English Language, Physics, Chemistry and any other science subject.";
const ndAdmissionText = "ND programme applicant will meet the O’Level requirement in addition to a prescribe JAMB (UTME) score for the year.";
const hndAdmissionText = "HND programme will meet the O’Level requirement and would have obtained his or her ND certificate.";

const ndCourseStructure = [
  "Electrical Drawings",
  "Introduction to Digital Electronics",
  "Technical Documentation and Report Writing",
  "Electrical Engineering Science",
  "Industrial Health and safety",
  "Electrical Workshop Practice",
  "Electrical Power",
  "Electrical Machine",
  "Electronics",
  "Electrical and Electronics Measurement and Instrumentation",
  "Telecommunication",
  "Electrical Installation of Buildings",
  "Electric Circuit Theory",
  "Use of Electrical and Electronics Instruments",
  "Computer Hardware and Software",
  "Research Methods in Electrical Engineering",
  "Introduction to Industrial Automation",
  "Project",
  "Electrical/Electronic Maintenance and Repairs",
  "Electrical Drawings",
  "Introduction to Digital Electronics",
  "Technical Documentation and Report Writing",
  "Electrical Engineering Science",
  "Industrial Health and safety",
  "Electrical Workshop Practice"
];

const hndElectronicsTelecom = [
  "ELECTRICAL MATERIAL SCIENCE",
  "ELECTRICAL MEASUREMENT AND CONTROL",
  "ELECTRIC CIRCUIT THEORY",
  "ANALOGUE ELECTRONICS",
  "INDUSTRIAL ELECTRONICS",
  "Testing Method and Reliability",
  "Electrical Theory",
  "Electrical Design and Drafting",
  "Digital Communication",
  "Electrical power system",
  "Electrical Machine",
  "Data communication and Networking",
  "Electromagnetic Field Theory",
  "Electronics  Design and Drafting",
  "Electronic measurement and & Control",
  "Microprocessor Applications",
  "Control Engineering System",
  "Electronic Maintenance & Repairs",
  "Computer Hardware Maintenance & Repair",
  "Project   II (Main Project)"
];

const hndPowerMachine = [
  "ELECTRICAL MATERIAL SCIENCE",
  "ELECTRICAL MEASUREMENT AND CONTROL",
  "ELECTRIC CIRCUIT THEORY",
  "ANALOGUE ELECTRONICS",
  "INDUSTRIAL ELECTRONICS",
  "Testing Method and Reliability",
  "Electrical Theory",
  "Electrical Design and Drafting",
  "Digital Communication",
  "Electrical power system",
  "Electrical Machine",
  "Data communication and Networking",
  "Electromagnetic Field Theory",
  "Control Engineering System",
  "Electrical Measurement & Control",
  "Electrical Maintenance and Repairs",
  "Project   II (Main Project)"
];

const brochureCards = [
  {
    icon: Zap,
    title: "Pioneer Department",
    text: "Established in 1977 with a long tradition of Electrical/Electronics Engineering education at The Federal Polytechnic, Bida.",
  },
  {
    icon: GraduationCap,
    title: "ND, HND & B.Eng Pathways",
    text: "The department runs ND and HND Electrical/Electronics Engineering programmes and a B.Eng programme affiliated with the Federal University of Technology, Minna.",
  },
  {
    icon: CircuitBoard,
    title: "Centre of Excellence",
    text: "Federal Polytechnic Bida was selected as a Centre of Excellence in Electrical/Electronic Engineering in 1989, with major equipment support arriving in 1990.",
  },
  {
    icon: Gauge,
    title: "Laboratories & Practical Training",
    text: "Facilities cover Electrical Power, Electrical Machines, Telecommunications, Electronics, Instrumentation and Control, Computer, and Basic Electricity laboratories.",
  },
  {
    icon: Sun,
    title: "Emerging Technologies",
    text: "Students are exposed to renewable energy, automation, smart grids, embedded systems, IoT and modern communication technologies.",
  },
  {
    icon: BriefcaseBusiness,
    title: "Career & Enterprise",
    text: "Graduates can pursue professional careers or entrepreneurship in electrical installation, solar systems, electronics, automation, telecommunications and related services.",
  },
];

const galleryImages = [
  {
    img: "electrical-gallery1.jpg",
    title: "Power Systems & Electrical Machines",
    caption: "Representative Electrical Engineering power, machines and laboratory practice.",
  },
  {
    img: "electrical-gallery2.jpg",
    title: "Electronics & Telecommunications",
    caption: "Representative electronics, communication and digital systems learning activities.",
  },
  {
    img: "electrical-gallery3.jpg",
    title: "Automation & Control",
    caption: "Representative instrumentation, automation, PLC and control-system practice.",
  },
  {
    img: "electrical-gallery4.jpg",
    title: "Renewable Energy & Projects",
    caption: "Representative renewable-energy, electrical installation and student project activities.",
  },
  {
    img: "electrical-gallery5.jpg",
    
  },
  {
    img: "electrical-gallery6.jpg",
    
  },
  {
    img: "electrical-gallery7.jpg",
    
  },
  {
    img: "electrical-gallery8.jpg",
    
  },
];

const StaffAcademicProfileModal = ({ staff, onClose }) => {
  if (!staff) return null;

  const qualifications = cleanArray(staff.qualifications);
  const memberships = cleanArray(staff.professionalQualifications);
  const research = cleanArray(staff.researchInterests);
  const specialties = cleanArray(staff.specialties);
  const awards = cleanArray(staff.awards);
  const publications = cleanText(staff.publications);
  const email = cleanText(staff.email);
  const phone = cleanText(staff.phone);
  const name = fullStaffName(staff);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-3 sm:p-5 md:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 18 }}
        transition={{ duration: 0.25, ease: "easeOut" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="electrical-staff-profile-title"
        className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto rounded-2xl md:rounded-3xl bg-white shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close staff academic profile"
          className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:bg-slate-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
        >
          <X size={21} />
        </button>

        <div className="grid md:grid-cols-[340px_minmax(0,1fr)]">
          <aside className="relative overflow-hidden bg-gradient-to-b from-slate-950 via-blue-950 to-blue-800 p-7 pt-16 text-white md:p-8">
            <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-blue-400/10 blur-3xl" />

            <div className="relative z-10">
              <div className="mx-auto aspect-[4/5] w-full max-w-[280px] overflow-hidden rounded-2xl border-4 border-white/20 bg-slate-200 shadow-2xl">
                <img
                  src={staff.image}
                  alt={name}
                  className="h-full w-full object-cover object-top"
                  onError={(event) => {
                    event.currentTarget.src = "/images/default-staff.jpg";
                  }}
                />
              </div>

              <div className="mt-5 flex justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold text-blue-50">
                  <BadgeCheck size={15} />
                  {cleanText(staff.rank) || "Department Staff"}
                </span>
              </div>

              <div className="mt-6 text-center md:text-left">
                <h2 className="text-2xl font-extrabold leading-tight">{name}</h2>
                <p className="mt-2 text-sm font-medium text-blue-200">
                  Department of Electrical Engineering
                </p>
              </div>

              {email && (
                isUsableEmail(email) ? (
                  <a
                    href={`mailto:${email}`}
                    className="mt-5 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100 transition hover:bg-white/10"
                  >
                    <Mail size={18} className="shrink-0 text-blue-300" />
                    <span className="break-all">{email}</span>
                  </a>
                ) : (
                  <div className="mt-5 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100">
                    <Mail size={18} className="shrink-0 text-blue-300" />
                    <span className="break-all">{email}</span>
                  </div>
                )
              )}

              {phone && (
                isUsablePhone(phone) ? (
                  <a
                    href={`tel:${phone.replace(/\D/g, "")}`}
                    className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100 transition hover:bg-white/10"
                  >
                    <Phone size={18} className="shrink-0 text-blue-300" />
                    <span>{phone}</span>
                  </a>
                ) : (
                  <div className="mt-3 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-blue-100">
                    <Phone size={18} className="shrink-0 text-blue-300" />
                    <span>{phone}</span>
                  </div>
                )
              )}
            </div>
          </aside>

          <div className="min-w-0 p-6 sm:p-8 md:p-10 lg:p-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              Department Staff
            </p>
            <h3
              id="electrical-staff-profile-title"
              className="mt-2 text-2xl font-extrabold text-slate-900 sm:text-3xl"
            >
              Staff Academic Profile
            </h3>
            <div className="mt-4 h-1 w-16 rounded-full bg-blue-700" />

            {qualifications.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <GraduationCap size={20} className="text-blue-700" />
                  Academic Qualifications
                </h4>
                <div className="mt-4 space-y-3">
                  {qualifications.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
                    >
                      <CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />
                      <p className="text-sm leading-6 text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {memberships.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <ShieldCheck size={19} className="text-blue-700" />
                  Professional Qualifications
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {memberships.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {publications && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <BookOpen size={19} className="text-blue-700" />
                  Academic Publications
                </h4>
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700">
                  {publications}
                </div>
              </section>
            )}

            {research.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <BookOpenCheck size={19} className="text-blue-700" />
                  Research Interests
                </h4>
                <div className="mt-4 flex flex-wrap gap-2">
                  {research.map((item, index) => (
                    <span
                      key={index}
                      className="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {specialties.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <DraftingCompass size={19} className="text-blue-700" />
                  Areas of Specialty
                </h4>
                <div className="mt-4 space-y-3">
                  {specialties.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-xl border border-slate-200 px-4 py-3 text-sm leading-6 text-slate-700"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {awards.length > 0 && (
              <section className="mt-8">
                <h4 className="flex items-center gap-3 text-lg font-bold text-slate-900">
                  <Award size={19} className="text-amber-600" />
                  Academic Awards / Breakthroughs
                </h4>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {awards.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50/70 p-4"
                    >
                      <Award size={18} className="mt-0.5 shrink-0 text-amber-600" />
                      <span className="text-sm font-medium leading-6 text-slate-700">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   GALLERY LIGHTBOX
========================================================= */

const GalleryLightbox = ({ image, images, onClose, onChange }) => {
  if (!image) return null;

  const currentIndex = image.index;

  const changeImage = (direction) => {
    const nextIndex =
      direction === "next"
        ? currentIndex === images.length - 1
          ? 0
          : currentIndex + 1
        : currentIndex === 0
          ? images.length - 1
          : currentIndex - 1;

    onChange({ ...images[nextIndex], index: nextIndex });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/95 p-3 backdrop-blur-md sm:p-5 md:p-8"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="relative w-full max-w-6xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close gallery"
          className="absolute right-3 top-3 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-800 shadow-xl hover:text-blue-700"
        >
          <X size={22} />
        </button>

        <button
          type="button"
          onClick={() => changeImage("previous")}
          aria-label="Previous image"
          className="absolute left-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-xl hover:text-blue-700 md:left-5"
        >
          <ChevronLeft size={27} />
        </button>

        <button
          type="button"
          onClick={() => changeImage("next")}
          aria-label="Next image"
          className="absolute right-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-800 shadow-xl hover:text-blue-700 md:right-5"
        >
          <ChevronRight size={27} />
        </button>

        <div className="overflow-hidden rounded-2xl bg-black shadow-2xl">
          <div className="flex min-h-[260px] items-center justify-center bg-black">
            <motion.img
              key={image.img}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              src={`${ELECTRICAL_IMAGE_BASE}/gallery/${image.img}`}
              alt={image.title}
              className="max-h-[78vh] w-full object-contain"
              onError={(event) => {
                event.currentTarget.src = `${ELECTRICAL_IMAGE_BASE}/electrical-engineering-hero.jpg`;
              }}
            />
          </div>

          <div className="bg-slate-900 px-5 py-4 text-white">
            <h3 className="font-semibold">{image.title}</h3>
            <p className="mt-1 text-sm text-slate-300">{image.caption}</p>
            <p className="mt-1 text-xs text-slate-500">
              Image {currentIndex + 1} of {images.length}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */



const ElectricalEngineering = () => {
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [staffSearch, setStaffSearch] = useState("");

  const filteredStaff = useMemo(() => {
    const query = staffSearch.trim().toLowerCase();
    if (!query) return electricalEngineeringStaff;

    return electricalEngineeringStaff.filter((staff) => {
      const haystack = [
        fullStaffName(staff),
        cleanText(staff.rank),
        ...cleanArray(staff.researchInterests),
        ...cleanArray(staff.specialties),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [staffSearch]);

  const fadeUp = {
    hidden: { opacity: 1, y: 18 },
    visible: { opacity: 1, y: 0 },
  };

  useEffect(() => {
    if (!selectedStaff && !selectedGalleryImage) return undefined;

    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedStaff(null);
        setSelectedGalleryImage(null);
      }

      if (selectedGalleryImage && event.key === "ArrowLeft") {
        const index = selectedGalleryImage.index;
        const nextIndex = index === 0 ? galleryImages.length - 1 : index - 1;
        setSelectedGalleryImage({ ...galleryImages[nextIndex], index: nextIndex });
      }

      if (selectedGalleryImage && event.key === "ArrowRight") {
        const index = selectedGalleryImage.index;
        const nextIndex = index === galleryImages.length - 1 ? 0 : index + 1;
        setSelectedGalleryImage({ ...galleryImages[nextIndex], index: nextIndex });
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedStaff, selectedGalleryImage]);

  return (
    <div className="min-h-screen bg-gray-50 text-slate-900">
      {/* DEPARTMENT NAVIGATION */}
      <nav className="sticky top-0 z-50 flex items-center gap-6 overflow-x-auto whitespace-nowrap bg-white/90 px-4 py-3 text-sm shadow-md backdrop-blur-md md:px-20">
        <div className="flex-1 font-semibold text-blue-700">
          Electrical Engineering — Federal Polytechnic Bida
        </div>

        <div className="flex gap-4">
          <a href="#brief" className="hover:text-blue-500">Overview</a>
          <a href="#why" className="hover:text-blue-500">Why Study</a>
          <a href="#admission" className="hover:text-blue-500">Admission</a>
          <a href="#prospects" className="hover:text-blue-500">Careers</a>
          <a href="#certifications" className="hover:text-blue-500">Certifications</a>
          <a href="#staff" className="hover:text-blue-500">Meet Staff</a>
          <a href="#courses" className="hover:text-blue-500">Courses</a>
          <a href="#brochure" className="hover:text-blue-500">Brochure</a>
          <a href="#gallery" className="hover:text-blue-500">Gallery</a>
          <a href="#contact" className="hover:text-blue-500">Contact</a>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative flex h-[340px] items-center justify-center overflow-hidden md:h-[420px]">
        <img
          src={`${ELECTRICAL_IMAGE_BASE}/el.jpg`}
          alt="Electrical Engineering technology and infrastructure"
          className="absolute inset-0 h-full w-full scale-105 object-cover"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = "/images/department-hero.jpg";
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-blue-950/75 to-slate-950/45" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.25),transparent_35%)]" />

        <motion.div
          aria-hidden="true"
          className="absolute left-[7%] top-10 hidden h-16 w-16 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-cyan-100 backdrop-blur-md md:flex"
          animate={{ y: [0, -8, 0], rotate: [0, 3, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
          <Zap size={30} />
        </motion.div>

        <motion.div
          aria-hidden="true"
          className="absolute right-[10%] top-14 hidden h-14 w-14 items-center justify-center rounded-2xl border border-white/15 bg-white/10 text-cyan-100 backdrop-blur-md md:flex"
          animate={{ y: [0, 7, 0], rotate: [0, -4, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <CircuitBoard size={27} />
        </motion.div>

        <div className="relative z-10 px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-cyan-50 backdrop-blur-md"
          >
            <Zap size={17} className="text-cyan-200" />
            Electrical Engineering Technology
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
            className="text-3xl font-extrabold md:text-5xl"
          >
            Department of Electrical Engineering
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-3 max-w-3xl"
          >
            Powering the future through practical electrical and electronics engineering education, innovation, modern technology and professional development.
          </motion.p>

          <div className="mx-auto mt-5 flex max-w-4xl flex-wrap justify-center gap-2.5">
            {[
              { icon: Zap, label: "Power Systems" },
              { icon: CircuitBoard, label: "Electronics" },
              { icon: Radio, label: "Telecommunications" },
              { icon: Sun, label: "Renewable Energy" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <span
                  key={item.label}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/25 px-3 py-1.5 text-xs font-medium text-cyan-50 backdrop-blur-sm sm:text-sm"
                >
                  <Icon size={15} className="text-cyan-200" />
                  {item.label}
                </span>
              );
            })}
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <a
              href="/admissions"
              className="rounded-lg bg-blue-700 px-5 py-3 font-semibold text-white shadow transition hover:bg-blue-600"
            >
              Apply Now
            </a>
            <a
              href="#brochure"
              className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-blue-700 shadow hover:bg-blue-50"
            >
              <FileText size={18} />
              View Brochure
            </a>
          </div>
        </div>
      </header>

      <main className="space-y-12 px-6 py-12 md:px-20">
        {/* DEPARTMENT IN BRIEF */}
        <motion.section
          id="brief"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <BookOpen className="text-blue-700" />
            The Department in Brief
          </h2>
          <Divider />

          <div className="space-y-4 text-justify leading-relaxed text-gray-700">
            {departmentHistory.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </motion.section>

        {/* HOD SPEECH */}
        <motion.section
          id="hodspeech"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="relative flex flex-col items-start gap-10 overflow-hidden rounded-2xl bg-white p-8 shadow-lg md:flex-row"
        >
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-cyan-50/40 to-white" />

          <div className="relative z-10 flex w-full justify-center md:w-1/3">
            <motion.img
              src={hodProfile.image}
              alt={`${hodProfile.name}, Head of Department`}
              className="h-52 w-52 rounded-full border-4 border-white object-cover object-top shadow-xl"
              whileHover={{ rotate: 2, scale: 1.04 }}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = "/images/default-staff.jpg";
              }}
            />
          </div>

          <div className="relative z-10 flex-1">
            <h2 className="flex items-center gap-3 text-2xl font-bold">
              <Users className="text-blue-700" />
              HOD Speech
            </h2>
            <Divider />

            <div className="mb-6 flex flex-wrap gap-3">
              {["Power Systems", "Electronics", "Telecommunications", "Automation & Control"].map((badge) => (
                <span
                  key={badge}
                  className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 shadow-sm"
                >
                  {badge}
                </span>
              ))}
            </div>

            <div className="mb-6 rounded-md border-l-4 border-cyan-600 bg-cyan-50 p-4 shadow-sm">
              <p className="font-medium italic text-blue-950">
                “...committed to providing students with quality education that combines sound theoretical knowledge with strong practical training.”
              </p>
            </div>

            <div className="space-y-4 text-justify leading-relaxed text-gray-700">
              {hodSpeechParagraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            <p className="mt-6 font-semibold text-blue-700">
              — {hodProfile.name}, {hodProfile.credentials}
            </p>
            <p className="text-gray-600">{hodProfile.role}</p>
            <p className="mt-1 text-sm text-gray-500">{hodProfile.qualification}</p>
          </div>
        </motion.section>

        {/* WHY STUDY */}
        <motion.section
          id="why"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-blue-900 p-8 text-white shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <Award className="text-yellow-300" />
            Why Study Here?
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-blue-100">
            {whyStudyIntro}
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {studyReasons.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-xl border border-white/10 bg-white/5 p-5 transition hover:bg-white/10"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-blue-800 shadow-sm">
                    <Icon size={20} />
                  </span>
                  <h3 className="mt-4 font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-6 rounded-xl border border-white/10 bg-white/10 p-5">
            <p className="text-justify leading-relaxed text-blue-50">
              {whyStudyClosing}
            </p>
          </div>
        </motion.section>

        {/* ADMISSION */}
        <motion.section
          id="admission"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <GraduationCap className="text-blue-700" />
            Admission Requirements
          </h2>
          <Divider />

          <p className="mb-6 text-justify leading-relaxed text-gray-700">
            {admissionIntro}
          </p>

          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-6">
              <h3 className="font-bold text-blue-950">National Diploma (ND)</h3>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-gray-700">
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Minimum of five O'Level credit passes at not more than two sittings.</li>
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Required subjects: Mathematics, English Language, Physics, Chemistry and any other science subject.</li>
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />{ndAdmissionText}</li>
              </ul>
            </div>

            <div className="rounded-xl border border-blue-100 bg-blue-50 p-6">
              <h3 className="font-bold text-blue-950">Higher National Diploma (HND)</h3>
              <ul className="mt-4 space-y-3 text-sm leading-6 text-gray-700">
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />Applicants must satisfy the relevant O'Level requirement.</li>
                <li className="flex gap-2"><CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />{hndAdmissionText}</li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* JOB PROSPECTS */}
        <motion.section
          id="prospects"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <BriefcaseBusiness className="text-blue-700" />
            Job Prospects
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-700">
            {jobProspectsIntro}
          </p>

          <h3 className="mt-7 text-lg font-semibold text-blue-800">
            Career opportunities include:
          </h3>

          <div className="mt-4 grid gap-x-8 gap-y-3 md:grid-cols-2">
            {careerOpportunities.map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-gray-700 transition hover:border-blue-100 hover:bg-blue-50/60"
              >
                <CheckCircle2 size={17} className="mt-1 shrink-0 text-blue-700" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="mt-7 space-y-4 rounded-xl border-l-4 border-blue-700 bg-blue-50 p-5">
            {jobProspectsClosing.map((paragraph) => (
              <p key={paragraph} className="text-justify leading-relaxed text-gray-700">
                {paragraph}
              </p>
            ))}
          </div>
        </motion.section>

        {/* CERTIFICATIONS */}
        <motion.section
          id="certifications"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-cyan-50 p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <ShieldCheck className="text-blue-700" />
            Professional Certifications and Courses
          </h2>
          <Divider />

          <p className="text-justify leading-relaxed text-gray-800">
            {professionalDevelopmentIntro}
          </p>

          <h3 className="mt-8 text-lg font-semibold text-blue-900">
            Recommended Areas
          </h3>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {professionalCourses.map((item) => {
              const Icon = item.icon;
              return (
                <article
                  key={item.title}
                  className="rounded-xl border border-cyan-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-blue-700">
                      <Icon size={18} />
                    </span>
                    <div>
                      <h4 className="font-semibold text-blue-900">{item.title}</h4>
                      {item.description && (
                        <p className="mt-1 text-sm leading-6 text-gray-700">
                          {item.description}
                        </p>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </motion.section>

        {/* STAFF */}
        <motion.section
          id="staff"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.02 }}
          className="scroll-mt-24 rounded-2xl bg-white p-6 shadow-lg md:p-8"
        >
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="flex items-center gap-3 text-2xl font-bold text-slate-900">
                <Users2 className="text-blue-700" />
                Meet Our Staff
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600 sm:text-base">
                Explore the academic, instructional and technical professionals contributing to Electrical Engineering teaching, laboratory practice, applied research, innovation and student development.
              </p>
            </div>

            <label className="relative block w-full lg:max-w-sm">
              <Search
                size={18}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={staffSearch}
                onChange={(event) => setStaffSearch(event.target.value)}
                placeholder="Search staff or specialization..."
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-4 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </div>
          <Divider />

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {filteredStaff.map((staff) => {
              const name = fullStaffName(staff);
              const research = cleanArray(staff.researchInterests);
              const specialties = cleanArray(staff.specialties);

              return (
                <motion.button
                  type="button"
                  key={staff.id}
                  onClick={() => setSelectedStaff(staff)}
                  whileHover={{ y: -7 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label={`View academic profile of ${name}`}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition hover:border-blue-200 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  <div className="relative h-[310px] overflow-hidden bg-slate-100">
                    <img
                      src={staff.image}
                      alt={name}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = "/images/default-staff.jpg";
                      }}
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/85 via-slate-950/35 to-transparent px-4 pb-4 pt-16">
                      <span className="rounded-full bg-blue-600/95 px-3 py-1 text-xs font-semibold text-white shadow">
                        {cleanText(staff.rank) || "Department Staff"}
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="text-lg font-bold text-slate-900">{name}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-600">
                      {research[0] || specialties[0] || "Electrical Engineering"}
                    </p>
                    <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-sm font-bold text-blue-700">
                      <span>View Academic Profile</span>
                      <ChevronRight size={19} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {filteredStaff.length === 0 && (
            <div className="rounded-xl bg-slate-50 p-8 text-center text-slate-600">
              No staff profile matches your search.
            </div>
          )}
        </motion.section>

        {/* COURSE STRUCTURE */}
        <motion.section
          id="courses"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.02 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <CircuitBoard className="text-blue-700" />
            Course Structure
          </h2>
          <Divider />

          <p className="text-sm leading-6 text-slate-600">
            The departmental write-up provides the following major course lists for the National Diploma and the two Higher National Diploma options.
          </p>

          <div className="mt-7 grid gap-7 xl:grid-cols-3">
            <div>
              <h3 className="text-xl font-bold text-blue-800">Major Course Structure (ND)</h3>
              <div className="mt-4 rounded-xl bg-slate-50 p-5">
                <ol className="space-y-1.5 text-sm text-slate-700">
                  {ndCourseStructure.map((course, index) => (
                    <li key={`${course}-${index}`} className="break-inside-avoid">{course}</li>
                  ))}
                </ol>
                <p className="mt-4 text-xs leading-5 text-slate-500">
                  Note: the supplied departmental ND list repeats six courses at the end; they are retained here to match the source document.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-blue-800">HND — Electronics/Telecommunications</h3>
              <div className="mt-4 rounded-xl bg-blue-50/70 p-5">
                <ol className="space-y-1.5 text-sm text-slate-700">
                  {hndElectronicsTelecom.map((course) => (
                    <li key={course} className="break-inside-avoid">{course}</li>
                  ))}
                </ol>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold text-blue-800">HND — Power & Machine</h3>
              <div className="mt-4 rounded-xl bg-cyan-50/70 p-5">
                <ol className="space-y-1.5 text-sm text-slate-700">
                  {hndPowerMachine.map((course) => (
                    <li key={course} className="break-inside-avoid">{course}</li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </motion.section>

        {/* BROCHURE */}
        <motion.section
          id="brochure"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-blue-950 p-7 text-white shadow-lg md:p-8"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="flex items-center gap-3 text-2xl font-bold">
                <FileText />
                Electrical Engineering Admission Brochure
              </h2>
              <p className="mt-2 text-sm leading-6 text-blue-100">
                A website-ready programme overview developed from the supplied Electrical Engineering departmental write-up.
              </p>
            </div>

            <a
              href="/electrical-engineering-brochure.pdf"
              download
              className="inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2 font-semibold text-blue-950"
            >
              <Download size={18} />
              Download PDF
            </a>
          </div>
          <Divider />

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {brochureCards.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-xl border border-white/10 bg-white/10 p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-4 font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-blue-100">{item.text}</p>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* GALLERY */}
        <motion.section
          id="gallery"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-6 shadow-lg md:p-8"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <CircuitBoard className="text-blue-700" />
            Department Gallery
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-600 sm:text-base">
            Explore Electrical Engineering laboratories, practical training, power systems, electronics, communications, automation and student projects.
          </p>
          <Divider />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {galleryImages.map((image, index) => (
              <motion.button
                type="button"
                key={image.img}
                onClick={() => setSelectedGalleryImage({ ...image, index })}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="group overflow-hidden rounded-xl border border-slate-200 bg-slate-100 text-left shadow-sm transition hover:border-blue-200 hover:shadow-lg"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={`${ELECTRICAL_IMAGE_BASE}/gallery/${image.img}`}
                    alt={image.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = `${ELECTRICAL_IMAGE_BASE}/electrical-engineering-hero.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/5 to-transparent" />
                  <div className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-blue-700 shadow-lg">
                    <Maximize2 size={17} />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <p className="font-semibold">{image.title}</p>
                    <p className="mt-1 text-xs text-slate-200">Click to View</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.section>

        {/* CONTACT */}
        <motion.section
          id="contact"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.03 }}
          className="scroll-mt-24 rounded-2xl bg-white p-8 shadow-lg"
        >
          <h2 className="flex items-center gap-3 text-2xl font-bold">
            <MapPin className="text-blue-700" />
            Contact the Department
          </h2>
          <Divider />

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-gray-700">
                <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
              </p>

              <p className="mt-3 flex items-center gap-2 text-gray-700">
                <Mail size={19} className="text-blue-700" />
                <a href={`mailto:${hodProfile.email}`} className="hover:text-blue-700">
                  {hodProfile.email}
                </a>
              </p>

              <p className="mt-3 flex items-center gap-2 text-gray-700">
                <Phone size={19} className="text-blue-700" />
                <a href="tel:+2348036374037" className="hover:text-blue-700">
                  {hodProfile.phone}
                </a>
              </p>

              <div className="mt-6 rounded-xl border border-cyan-100 bg-cyan-50 p-5">
                <h3 className="font-semibold text-blue-950">Department Contact</h3>
                <p className="mt-2 text-sm leading-6 text-blue-900">
                  Prospective students, parents, industry partners and other visitors may contact the Department using the official email address and telephone number above.
                </p>
              </div>
            </div>

            <div>
              <iframe
                title="Federal Polytechnic Bida location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus"
                width="100%"
                height="280"
                loading="lazy"
                className="rounded-md border-0 shadow-sm"
              />
            </div>
          </div>
        </motion.section>
      </main>

      <AnimatePresence>
        {selectedStaff && (
          <StaffAcademicProfileModal
            staff={selectedStaff}
            onClose={() => setSelectedStaff(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedGalleryImage && (
          <GalleryLightbox
            image={selectedGalleryImage}
            images={galleryImages}
            onClose={() => setSelectedGalleryImage(null)}
            onChange={setSelectedGalleryImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ElectricalEngineering;
